// Shared "feature some product photos in an outreach email" logic, used by
// both OutreachTab (pitching businesses) and MarketsTab (pitching market
// organisers) — pulled out once both needed the exact same picker/photo-row
// behaviour rather than duplicating it.
import { useEffect, useMemo, useState } from 'react';
import { api, API_BASE } from './auth';

// Mirrors src/pages/productsPage.js's publicImageUrl() exactly — product
// photos are stored as server-relative paths (/api/uploads/images/...);
// an email needs a full https:// URL to actually load the image.
export const publicImageUrl = (u) =>
  String(u).startsWith('/api') ? API_BASE + String(u).slice(4) : u;

export const MAX_FEATURED_PRODUCTS = 3;

// Curated lead photo per crop folder — same file this repo's productImages.js
// (in the newnewtdh site) picks as each crop's best/lead shot, so emails use
// the same photography the website itself leads with. These exist on disk
// under newnewtdh's public/images/products/ but aren't in any product's
// uploaded `images[]` array, so they're offered as extra picker options
// alongside whatever's in the farm-dashboard catalogue.
export const WEBSITE_CROP_PHOTOS = [
  { key: 'genoBasil',    name: 'Genovese Basil', url: 'https://teardrophydro.co.uk/images/products/genoBasil/IMG_5987.JPEG' },
  { key: 'salvoBasil',   name: 'Salvo Basil',    url: 'https://teardrophydro.co.uk/images/products/salvoBasil/IMG_4565.JPEG' },
  { key: 'chives',       name: 'Chives',         url: 'https://teardrophydro.co.uk/images/products/chives/IMG_4857.JPEG' },
  { key: 'cress',        name: 'Cress',          url: 'https://teardrophydro.co.uk/images/products/cress/IMG_4581.JPEG' },
  { key: 'dill',         name: 'Dill',           url: 'https://teardrophydro.co.uk/images/products/dill/IMG_5227.JPEG' },
  { key: 'komatsuna',    name: 'Komatsuna',      url: 'https://teardrophydro.co.uk/images/products/komatsuna/IMG_4483.JPEG' },
  { key: 'lettuce',      name: 'Lettuce',        url: 'https://teardrophydro.co.uk/images/products/lettuce/IMG_4447.JPEG' },
  { key: 'sorrel',       name: 'Sorrel',         url: 'https://teardrophydro.co.uk/images/products/sorrel/IMG_5044.JPEG' },
  { key: 'mint',         name: 'Mint',           url: 'https://teardrophydro.co.uk/images/products/mint/IMG_6827.JPEG' },
  { key: 'coriander',    name: 'Coriander',      url: 'https://teardrophydro.co.uk/images/products/coriander/IMG_6755.JPEG' },
  { key: 'mizuna',       name: 'Mizuna',         url: 'https://teardrophydro.co.uk/images/products/mizuna/IMG_6786.JPEG' },
  { key: 'frenchMix',    name: 'French Mix',     url: 'https://teardrophydro.co.uk/images/products/frenchMix/IMG_6777.JPEG' },
  { key: 'mesclunMix',   name: 'Mesclun Mix',    url: 'https://teardrophydro.co.uk/images/products/mesclunMix/IMG_6779.JPEG' },
  { key: 'leafBeet',     name: 'Leaf Beet',      url: 'https://teardrophydro.co.uk/images/products/leafBeet/IMG_6901.JPEG' },
  { key: 'parsley',      name: 'Parsley',        url: 'https://teardrophydro.co.uk/images/products/parsley/IMG_6914.JPEG' },
  { key: 'redAmaranth',  name: 'Red Amaranth',   url: 'https://teardrophydro.co.uk/images/products/redAmaranth/IMG_7317.JPEG' },
];

export const FALLBACK_PHOTOS = [
  { url: 'https://teardrophydro.co.uk/images/products/lettuce/IMG_4447.JPEG', alt: 'Fresh salad leaves' },
  { url: 'https://teardrophydro.co.uk/images/products/genoBasil/IMG_4479.JPEG', alt: 'Fresh basil' },
  { url: 'https://teardrophydro.co.uk/images/products/cress/IMG_4581.JPEG', alt: 'Microgreens' },
];

export function photosTableHtml(photos) {
  // Inline width per cell (rather than a fixed 33.33% in <style>) so 1 or 2
  // selected photos still fill the row evenly instead of leaving a gap.
  const width = `${(100 / photos.length).toFixed(2)}%`;
  const cells = photos.map(p => `<td style="width:${width}"><img src="${p.url}" alt="${p.alt}" /></td>`).join('\n          ');
  return `<table class="photos" role="presentation" cellpadding="0" cellspacing="0">
        <tr>
          ${cells}
        </tr>
      </table>`;
}

// Fetches enabled catalogue products with a real uploaded photo, and tops
// them up with WEBSITE_CROP_PHOTOS for anything not already covered by an
// uploaded photo — gives a much wider selection than the catalogue alone.
export function useFeaturablePhotos() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get('/products')
      .then(r => setProducts((r.data || []).filter(p => p.enabled && p.images?.length)))
      .catch(() => {}); // non-critical — picker just falls back to the website crop photos
  }, []);

  return useMemo(() => {
    const fromCatalogue = products.map(p => ({ id: `cms:${p.id}`, name: p.name, url: publicImageUrl(p.images[0]) }));
    const covered = new Set(fromCatalogue.map(p => p.name.toLowerCase()));
    const fromWebsite = WEBSITE_CROP_PHOTOS
      .filter(p => !covered.has(p.name.toLowerCase()))
      .map(p => ({ id: `site:${p.key}`, name: p.name, url: p.url }));
    return [...fromCatalogue, ...fromWebsite];
  }, [products]);
}
